/**
 * Local top-10 table for asteroid survival (the original HighScoreRegistry, which never
 * shipped). Pure list operations plus guarded localStorage persistence.
 */
export interface HighscoreEntry {
	name: string;
	/** Asteroids destroyed. */
	score: number;
	map: string;
	rules: string;
	/** Seconds survived. */
	seconds: number;
	/** ISO date. */
	date: string;
}

export const HIGHSCORE_LIMIT = 10;
export const NAME_MAX_LENGTH = 12;
const KEY = "gravity.highscores.v1";
const NAME_KEY = "gravity.lastName.v1";

/** A score earns a place if it is positive and beats the table's lowest (or the table isn't full). */
export function qualifies(list: readonly HighscoreEntry[], score: number): boolean {
	if (score <= 0) return false;
	if (list.length < HIGHSCORE_LIMIT) return true;
	return score > (list[list.length - 1]?.score ?? 0);
}

/**
 * Inserts an entry keeping the table sorted (higher score first; on a tie the older entry
 * stays ahead) and trimmed to the limit. Returns the new table and the entry's rank (0-based,
 * -1 if it did not make it).
 */
export function insertEntry(
	list: readonly HighscoreEntry[],
	entry: HighscoreEntry,
): { list: HighscoreEntry[]; rank: number } {
	const index = list.findIndex((e) => entry.score > e.score);
	const at = index < 0 ? list.length : index;
	const next = [...list.slice(0, at), entry, ...list.slice(at)].slice(0, HIGHSCORE_LIMIT);
	return { list: next, rank: at < HIGHSCORE_LIMIT ? at : -1 };
}

export function cleanName(raw: string): string {
	const name = raw.replace(/\s+/g, " ").trim().slice(0, NAME_MAX_LENGTH);
	return name.length > 0 ? name : "Pilot";
}

/** Validates stored data; drops malformed entries. */
export function parseHighscores(raw: unknown): HighscoreEntry[] {
	if (!Array.isArray(raw)) return [];
	const valid = raw.filter(
		(e): e is HighscoreEntry =>
			typeof e === "object" &&
			e !== null &&
			typeof e.name === "string" &&
			typeof e.score === "number" &&
			Number.isFinite(e.score) &&
			typeof e.map === "string" &&
			typeof e.rules === "string" &&
			typeof e.seconds === "number" &&
			typeof e.date === "string",
	);
	return [...valid].sort((a, b) => b.score - a.score).slice(0, HIGHSCORE_LIMIT);
}

/**
 * One board per survival variant: asteroids destroyed (the default) or 2008 scoring (P11.5),
 * each with legacy asteroid spawns (at the spawn points) or the safe asteroid spawns rule.
 */
export type HighscoreBoard = "asteroids" | "2008" | "asteroids-safe" | "2008-safe";

export const HIGHSCORE_BOARDS: readonly HighscoreBoard[] = [
	"asteroids",
	"asteroids-safe",
	"2008",
	"2008-safe",
];

const BOARD_KEYS: Record<HighscoreBoard, string> = {
	asteroids: KEY,
	"2008": "gravity.highscores.2008.v1",
	"asteroids-safe": "gravity.highscores.safe.v1",
	"2008-safe": "gravity.highscores.2008-safe.v1",
};

function keyOf(board: HighscoreBoard): string {
	return BOARD_KEYS[board];
}

/** The board a survival run counts for. */
export function highscoreBoard(scoring2008: boolean, safeAsteroidSpawns: boolean): HighscoreBoard {
	if (scoring2008) return safeAsteroidSpawns ? "2008-safe" : "2008";
	return safeAsteroidSpawns ? "asteroids-safe" : "asteroids";
}

/** Uses 2008 scoring (a Score column) rather than asteroids destroyed. */
export function isScoreBoard(board: HighscoreBoard): boolean {
	return board === "2008" || board === "2008-safe";
}

export function boardLabel(board: HighscoreBoard): string {
	const base = isScoreBoard(board) ? "Survival · 2008 scoring" : "Asteroid survival";
	return board.endsWith("-safe") ? `${base} · safe spawns` : base;
}

export function loadHighscores(board: HighscoreBoard = "asteroids"): HighscoreEntry[] {
	try {
		const raw = window.localStorage.getItem(keyOf(board));
		return raw ? parseHighscores(JSON.parse(raw)) : [];
	} catch {
		return [];
	}
}

export function saveHighscores(
	list: readonly HighscoreEntry[],
	board: HighscoreBoard = "asteroids",
): void {
	try {
		window.localStorage.setItem(keyOf(board), JSON.stringify(list));
	} catch {
		// Storage unavailable: the table just won't persist.
	}
}

export function loadLastName(): string {
	try {
		return window.localStorage.getItem(NAME_KEY) ?? "";
	} catch {
		return "";
	}
}

export function saveLastName(name: string): void {
	try {
		window.localStorage.setItem(NAME_KEY, name);
	} catch {
		// ignore
	}
}
